import React from "react";
import { SubscriptionActionFooter } from "./SubscriptionActionDialog";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import DiscountPreview from "./SubscriptionDiscountPreview";
import { parseDiscountInput } from "./subscriptionPricing";

const DISCOUNT_PERCENT_INPUT_REGEX = /^\d*(\.\d{0,1})?$/;

export default function SubscriptionDiscountAction({
  sub,
  discountValue,
  setDiscountValue,
  discountUnchanged,
  submitDiscount,
  updating,
  refreshing,
  pricingError,
}) {
  const parsedDiscount = parseDiscountInput(discountValue);
  const isInvalid = discountValue !== "" && parsedDiscount == null;

  const isApplyDisabled =
    parsedDiscount == null ||
    discountUnchanged ||
    updating ||
    refreshing ||
    Boolean(pricingError);

  return (
    <Stack spacing={2.5}>
      {/* Discount Input */}
      <Stack spacing={1}>
        <Typography variant='body2' fontWeight={600}>
          Discount percentage
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <TextField
            size='small'
            fullWidth
            value={discountValue}
            error={isInvalid}
            placeholder='0.00'
            inputProps={{
              inputMode: "decimal",
              min: 0,
              max: 100,
              "aria-label": "Discount percentage",
            }}
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || DISCOUNT_PERCENT_INPUT_REGEX.test(value)) {
                setDiscountValue(value);
              }
            }}
            sx={{
              maxWidth: { sm: 200 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
              },
            }}
          />

          <SubscriptionActionFooter>
            <Button
              variant='contained'
              onClick={submitDiscount}
              disabled={isApplyDisabled}
              disableElevation
              startIcon={
                updating ? <CircularProgress size={16} color='inherit' /> : null
              }
              sx={{
                minHeight: 40,
                px: 2.5,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {updating ? "Applying..." : "Apply Discount"}
            </Button>
          </SubscriptionActionFooter>
        </Stack>

        <Typography
          variant='caption'
          color={isInvalid ? "error.main" : "text.secondary"}
        >
          {isInvalid
            ? "Enter a valid percentage between 0 and 100."
            : "Enter a value between 0% and 100%."}
        </Typography>
      </Stack>

      {/* Preview */}
      <Box
        sx={{
          p: 2,
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <VisibilityOutlined
              sx={{ fontSize: 18, color: "text.secondary" }}
            />
            <Typography variant='body2' fontWeight={600}>
              Discount Preview
            </Typography>
          </Stack>

          <DiscountPreview subscription={sub} percent={parsedDiscount} />
        </Stack>
      </Box>
    </Stack>
  );
}
